import { memo } from 'react'
import { Plus } from 'lucide-react'
import type { Product } from '../../types'
import { cardBgClasses, CategoryIcon, unitLabels } from '../../lib/catalogConfig'
import StatusBadge from '../../components/ui/StatusBadge'

export const CardWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className="transition-transform duration-300 ease-out hover:-translate-y-[5px] pb-[5px] will-change-transform h-full">
    {children}
  </div>
)

const ProductCard = memo(({ product, index, onClick, isFavorite, onFavoriteToggle }: {
  product: Product
  index: number
  onClick: () => void
  isFavorite?: boolean
  onFavoriteToggle?: (e: React.MouseEvent) => void
}) => {
  const bg = cardBgClasses[index % cardBgClasses.length]

  return (
    <article
      onClick={onClick}
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick()}
      aria-label={`${product.name}, ₪${product.price} ל${unitLabels[product.unit]}`}
      className={`bg-white rounded-[20px] border-2 border-[#F2EDE4] overflow-hidden cursor-pointer
                 transition-[box-shadow] duration-300 ease-out h-full
                 shadow-[0_2px_12px_rgba(44,24,16,0.07)]
                 hover:shadow-[0_12px_32px_rgba(44,24,16,0.11)]
                 focus-visible:outline-none
                 focus-visible:ring-2 focus-visible:ring-[#2D6A4F] focus-visible:ring-offset-2`}
    >
      <div className={`h-40 relative overflow-hidden
        ${product.images?.[0] ? '' : `flex items-center justify-center text-[64px] ${bg}`}`}
      >
        {product.images?.[0]
          ? <div className="card-bg-peach h-full">
              <img
                src={product.images[0]}
                alt={product.name}
                className="w-full h-full object-cover"
                loading={index === 0 ? 'eager' : 'lazy'}
                fetchPriority={index === 0 ? 'high' : 'auto'}
                width={400}
                height={160}
                onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
              />
              <div className="flex items-center justify-center h-full w-full">
                <CategoryIcon category={product.category} className="w-22 h-22 lg:w-18 lg:h-18 mt-0 lg:mt-2.5 text-[#EAE4DB]" />
              </div>
            </div>
          : <CategoryIcon category={product.category} className="w-10 h-10 text-[#A89F97]" />
        }

        <span className="absolute top-3 left-3">
          <StatusBadge status={product.status} />
        </span>

        {/* <button
          onClick={onFavoriteToggle}
          aria-label={isFavorite ? `הסר ${product.name} ממועדפים` : `הוסף ${product.name} למועדפים`}
          aria-pressed={isFavorite}
          className={`absolute top-2.5 right-2.5 w-8 h-8 rounded-full flex items-center justify-center
                      transition-all duration-200 border-none cursor-pointer z-10
                      ${isFavorite
                        ? 'bg-[#E74C3C] shadow-[0_2px_8px_rgba(231,76,60,0.4)]'
                        : 'bg-white/80 hover:bg-white shadow-[0_2px_6px_rgba(44,24,16,0.15)]'
                      }`}
        >
          <Heart className={`w-4 h-4 transition-all duration-200 ${isFavorite ? 'fill-white text-white scale-110' : 'text-[#A89F97]'}`} />
        </button> */}
      </div>

      <div className="px-4 pt-4 pb-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-base font-bold text-[#1A1A1A] mb-0.5 truncate min-w-0">{product.name}</p>
        {(product.custom_sku || product.sku) && (
          <p className="text-[11px] text-[#6E6660] mb-1 shrink-0">מק״ט {product.custom_sku ?? product.sku}</p>
        )}
      </div>
        <p className="text-[13px] text-[#6E6660] leading-relaxed mb-3 h-fit lg:h-[42.25px] line-clamp-2">{product.description}</p>
        <div className="flex items-center justify-between">
        <span className="text-lg font-bold text-[#2D6A4F]">
          ₪{product.price}
          <span className="text-[13px] font-normal text-[#6E6660]"> / {unitLabels[product.unit]}</span>
        </span>
        <div className="flex items-center gap-4 lg:gap-3">
          <span className="text-xs text-[#6B6560] bg-[#F2EDE4] px-2.5 py-1 rounded-lg">
            {product.status === 'sold_out' ? 'אזל' : `${product.quantity} יח׳`}
          </span>
          {product.status !== 'sold_out' && (
            <button
              onClick={onFavoriteToggle}
              aria-label={isFavorite ? `הסר ${product.name} מהרשימה` : `הוסף ${product.name} לרשימה`}
              aria-pressed={isFavorite}
              className={`w-8 h-8 rounded-full flex items-center justify-center
                          transition-all duration-200 border-none cursor-pointer bg-[#2D6A4F]
                          ${isFavorite
                            ? 'bg-[#D8F3DC] shadow-[0_2px_8px_rgba(45,106,79,0.4)]'
                            : 'bg-[#F2EDE4] hover:bg-[#D8F3DC]'
                          }`}
            >
              <Plus className={`w-4 h-4 ${isFavorite ? 'text-[#2D6A4F]' : 'text-[#2D6A4F]'}`} />
            </button>
          )}
        </div>
      </div>
      </div>
    </article>
  )
})
ProductCard.displayName = 'ProductCard'

export default ProductCard